import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Settings2, AlertTriangle } from 'lucide-react';
import onboardingService from '../../services/onboardingService';

interface BrandContextBannerProps {
  /** When true, shows the "generating outside your usual pattern" copy instead of "following pattern". */
  ignorePattern?: boolean;
}

/**
 * Shared brand-context banner used by the AI generators (social suggestions AND blog content engine).
 * Shows the business profile the AI follows + a link to edit it. Single source of truth — no duplication.
 */
const BrandContextBanner: React.FC<BrandContextBannerProps> = ({ ignorePattern = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.resolve(onboardingService.getProfile())
      .then((p: any) => { if (active) setProfile(p?.data ?? p ?? null); })
      .catch(() => {})
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, []);

  if (!loaded) return null;

  const editProfile = () => navigate('/settings/business-profile');
  const name = profile?.business_name;
  const voice = profile?.brand_voice;

  if (!name) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {t('social.brandContext.missing', 'Conte à IA sobre seu negócio para resultados melhores.')}
          </p>
        </div>
        <button
          onClick={editProfile}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition"
        >
          <Settings2 className="w-3.5 h-3.5" />
          {t('social.brandContext.configure', 'Configurar')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
      <div className="flex items-start gap-2 min-w-0">
        <Sparkles className="w-5 h-5 text-purple-500 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {t('social.brandContext.title', 'Gerando com base em')}{' '}
            <strong className="font-semibold">{name}</strong>
            {voice && <span className="text-gray-500 dark:text-gray-400"> · {voice}</span>}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {ignorePattern
              ? t('social.brandContext.ignoringPattern', 'Gerando fora do seu padrão habitual.')
              : t('social.brandContext.followingPattern', 'A IA vai olhar seus últimos posts para manter o padrão.')}
          </p>
        </div>
      </div>
      <button
        onClick={editProfile}
        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition"
      >
        <Settings2 className="w-3.5 h-3.5" />
        {t('social.brandContext.edit', 'Editar')}
      </button>
    </div>
  );
};

export default BrandContextBanner;
